import {ReactNode, useState} from "react";
import {Box} from "@mui/material";
import {GridOn, DoorFront, Handyman, Image, ContentCut, ContentCopy, ContentPaste, Check} from "@mui/icons-material";
import {ElevationApp} from "../ElevationApp";
import {api} from "../../../../../api/API";
import {useEventEmitter2} from "../util/useEventEmitter2";
import {FrameProfileCtrls} from "./debug/FrameProfileCtrls";
import {TopDownCtrls} from "./debug/TopDownCtrls";
import {FrameGridCtrls} from "./debug/FrameGridCtrls";
import {FrameAllCtrls} from "./debug/FrameAllCtrls";
import {RibbonButton, RibbonGroup} from "./Ribbon";
import {frameLayoutDump} from "../scenes/frameGrid/debugFrameBlocks";

// the debug tab surfaces the contextual controls for whichever display view is
// currently active.
export function DebugTab(props: {
    app: ElevationApp;
}) {
    const activeView = useEventEmitter2(props.app.viewSettings.activeView);

    // copy the frame grid layout to the clipboard, to share when debugging. The label
    // reads "Copied" for a moment after.
    const [copied, setCopied] = useState(false);
    const copyLayout = () => {
        const text = frameLayoutDump(props.app.frameSpec, props.app.frameGrid);
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        }).catch(() => props.app.draw.showError("Can't copy to the clipboard"));
    };

    // load a frame design from the clipboard: a Copy Raw dump (its `design`), or a frame
    // design on its own (anything with `frameBlocks`).
    const pasteLayout = () => {
        navigator.clipboard.readText().then(text => {
            const parsed = JSON.parse(text);
            const design = parsed?.design ?? parsed;
            if(!design?.frameBlocks) throw new Error("no frameBlocks");
            props.app.loadFrameDesign(design);
        }).catch(e => {
            console.warn(e);
            props.app.draw.showError("Clipboard has no frame design");
        });
    };

    // the submittal renderings currently persisted on the opening (drw_*_rendering file
    // ids), attached by render-on-save. May be stale until the tool is reloaded.
    const opening = props.app.opening;
    const openStored = (fileId: number | undefined) => {
        if(!fileId) return;
        window.open(api.files.downloadLink(fileId, "inline"), "_blank");
    };

    let ctrls: ReactNode = null;
    switch(activeView) {
        case "frame-profile-active":
        case "frame-profile-inactive":
            ctrls = <FrameProfileCtrls app={props.app} />;
            break;
        case "top-down":
            ctrls = <TopDownCtrls app={props.app} />;
            break;
        case "frame-grid":
            ctrls = <FrameGridCtrls app={props.app} />;
            break;
        case "all":
            ctrls = <FrameAllCtrls app={props.app} />;
            break;
    }

    return (
        <>
            <RibbonGroup label="Export">
                <RibbonButton
                    icon={<GridOn />}
                    label="Screen Export"
                    active={activeView === "screen-export"}
                    onClick={() => props.app.showExport("screen")}
                />
                <RibbonButton
                    icon={<DoorFront />}
                    label="Door Export"
                    active={activeView === "door-export"}
                    onClick={() => props.app.showExport("door")}
                />
                <RibbonButton
                    icon={<Handyman />}
                    label="Hardware Export"
                    active={activeView === "hardware-export"}
                    onClick={() => props.app.showExport("hardware")}
                />
                <RibbonButton
                    icon={<ContentCut />}
                    label="Cut Plan Export"
                    active={activeView === "cutplan-export"}
                    onClick={() => props.app.showExport("cutplan")}
                />
            </RibbonGroup>
            <RibbonGroup label="Layout">
                <RibbonButton
                    icon={copied ? <Check /> : <ContentCopy />}
                    label={copied ? "Copied" : "Copy Raw"}
                    onClick={copyLayout}
                />
                <RibbonButton
                    icon={<ContentPaste />}
                    label="Paste Raw"
                    onClick={pasteLayout}
                />
            </RibbonGroup>
            <RibbonGroup label="Stored Renderings">
                <RibbonButton
                    icon={<Image />}
                    label="Screen"
                    disabled={!opening?.drwScreenRendering}
                    onClick={() => openStored(opening?.drwScreenRendering)}
                />
                <RibbonButton
                    icon={<Image />}
                    label="Door"
                    disabled={!opening?.drwDoorRendering}
                    onClick={() => openStored(opening?.drwDoorRendering)}
                />
                <RibbonButton
                    icon={<Image />}
                    label="Hardware"
                    disabled={!opening?.drwHardwareRendering}
                    onClick={() => openStored(opening?.drwHardwareRendering)}
                />
            </RibbonGroup>
            <RibbonGroup label="Frame">
                <Box sx={{display: "flex", alignItems: "center", gap: 1, py: 0.5}}>
                    {ctrls}
                </Box>
            </RibbonGroup>
        </>
    );
}
